import React, { useState, useEffect, useCallback } from 'react';

function PmsRevenueManager({ token, selectedProperty }) {
  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ margin: '0 0 0.5rem', color: 'var(--theme-primary)' }}>Competitor Settings</h2>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Manage this property's tracked competitor hotels and compare their live OTA rates against yours.
      </p>
      <RateShopTab token={token} selectedProperty={selectedProperty} />
    </div>
  );
}

const thStyle = { padding: '0.5rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.4rem 0.5rem', fontSize: '0.82rem' };

/* ─── Rate Shop tab: per-property competitor set (≤5) ─────────────────── */
const RESOLUTION_META = {
  PENDING: { label: 'Pending', color: '#2563eb', bg: '#eff6ff' },
  RESOLVED: { label: 'Resolved', color: '#16a34a', bg: '#f0fdf4' },
  ACTIVE: { label: 'Active', color: '#16a34a', bg: '#f0fdf4' },
  AMBIGUOUS: { label: 'Ambiguous', color: '#ca8a04', bg: '#fef9c3' },
  NOT_FOUND: { label: 'Not Found', color: '#dc2626', bg: '#fef2f2' },
};
const emptyCompetitor = { id: null, hotel_name: '', city: '', country: '', star_rating: '', segment: '', note: '' };

function RateShopTab({ token, selectedProperty }) {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyCompetitor);
  const [showForm, setShowForm] = useState(false);
  // Competitor Rates comparison grid
  const [rateRows, setRateRows] = useState([]);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateDays, setRateDays] = useState(14);
  const [yourRates, setYourRates] = useState({}); // { 'YYYY-MM-DD': '3200' } — eyeball comparison, not persisted

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/ai/revenue-manager/competitors', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load competitors');
      setCompetitors(data.competitors || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty, headers]);

  const loadRates = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setRateLoading(true);
    try {
      const res = await fetch(`/api/v1/ai/revenue-manager/competitor-rates?days=${rateDays}`, { headers: headers() });
      const data = await res.json();
      if (res.ok) setRateRows(Array.isArray(data.rows) ? data.rows : []);
    } catch { /* rates panel is best-effort */ }
    finally { setRateLoading(false); }
  }, [selectedProperty, headers, rateDays]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRates(); }, [loadRates]);

  const startAdd = () => { setForm(emptyCompetitor); setShowForm(true); setError(''); setMessage(''); };
  const startEdit = (c) => {
    setForm({ id: c.id, hotel_name: c.hotel_name || '', city: c.city || '', country: c.country || '',
      star_rating: c.star_rating || '', segment: c.segment || '', note: c.note || '' });
    setShowForm(true); setError(''); setMessage('');
  };

  const save = async () => {
    if (!form.hotel_name.trim()) { setError('Hotel name is required'); return; }
    if (!form.city.trim()) { setError('City is required'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch('/api/v1/ai/revenue-manager/competitors', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          id: form.id || undefined,
          hotel_name: form.hotel_name.trim(),
          city: form.city.trim(),
          country: form.country.trim() || null,
          star_rating: form.star_rating || null,
          segment: form.segment.trim() || null,
          note: form.note.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setMessage(form.id ? 'Competitor updated.' : 'Competitor added — pending resolution by the Rate Data Service.');
      setShowForm(false); setForm(emptyCompetitor);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove competitor "${c.hotel_name}"? Historical rates are preserved.`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/ai/revenue-manager/competitors/${c.id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove');
      setMessage('Competitor removed.');
      await load();
    } catch (err) { setError(err.message); }
  };

  if (!selectedProperty?.id) {
    return <div style={{ color: 'var(--theme-text-secondary)', padding: '1rem' }}>Select a property to manage its competitor set.</div>;
  }

  const atCap = competitors.length >= 5;

  return (
    <div>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Track up to <b>5 competitor hotels</b> for <b>{selectedProperty?.name || 'this property'}</b>. The EASEROOM Rate Data Service collects their live OTA rates (MMT, Agoda, Booking.com, Expedia). Pricing stays human-approved.
      </p>

      {error && <div style={{ background: '#fef2f2', color: 'var(--theme-danger)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #fecaca' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}
      {message && <div style={{ background: '#f0fdf4', color: 'var(--theme-success)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #bbf7d0' }}>{message} <button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-success)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem' }}>
        <button onClick={startAdd} disabled={atCap}
          style={{ padding: '0.45rem 1rem', background: atCap ? 'var(--theme-text-muted)' : 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 5, cursor: atCap ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          title={atCap ? 'Maximum of 5 competitors reached' : ''}>
          + Add Competitor
        </button>
        <span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>{competitors.length} / 5 competitors</span>
      </div>

      {showForm && (
        <div style={{ background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1rem 1.2rem', marginBottom: '1.2rem', maxWidth: 720 }}>
          <h4 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary-dark)' }}>{form.id ? 'Edit Competitor' : 'Add Competitor'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
            <Field label="Hotel Name *"><input value={form.hotel_name} onChange={e => setForm({ ...form, hotel_name: e.target.value })} maxLength={160} style={inputStyle} placeholder="ABC Grand" /></Field>
            <Field label="City / Locality *"><input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} maxLength={120} style={inputStyle} placeholder="Chennai" /></Field>
            <Field label="Country"><input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} maxLength={80} style={inputStyle} placeholder="IN" /></Field>
            <Field label="Star Rating">
              <select value={form.star_rating} onChange={e => setForm({ ...form, star_rating: e.target.value })} style={inputStyle}>
                <option value="">—</option>{[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{s} star</option>)}
              </select>
            </Field>
            <Field label="Market Segment"><input value={form.segment} onChange={e => setForm({ ...form, segment: e.target.value })} maxLength={40} style={inputStyle} placeholder="Upper Midscale" /></Field>
            <Field label="Note"><input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} style={inputStyle} placeholder="Optional" /></Field>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button onClick={save} disabled={saving} style={{ padding: '0.45rem 1.2rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              {saving ? 'Saving...' : (form.id ? 'Update' : 'Add')}
            </button>
            <button onClick={() => { setShowForm(false); setForm(emptyCompetitor); }} style={{ padding: '0.45rem 1.2rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: '#2563eb', marginBottom: '0.5rem' }}>Loading...</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={thStyle}>Hotel</th>
              <th style={thStyle}>City</th>
              <th style={thStyle}>Country</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Star</th>
              <th style={thStyle}>Segment</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Resolution</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {competitors.map(c => {
              const rm = RESOLUTION_META[c.resolution_status] || RESOLUTION_META.PENDING;
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{c.hotel_name}{c.resolved_name && c.resolved_name !== c.hotel_name && <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>→ {c.resolved_name}</span>}</td>
                  <td style={tdStyle}>{c.city}</td>
                  <td style={tdStyle}>{c.country || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.star_rating ? `${c.star_rating}★` : '-'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>{c.segment || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, color: rm.color, background: rm.bg }}>{rm.label}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button onClick={() => startEdit(c)} style={{ padding: '0.2rem 0.5rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', marginRight: 4 }}>Edit</button>
                    <button onClick={() => remove(c)} style={{ padding: '0.2rem 0.5rem', background: 'var(--theme-danger)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>Remove</button>
                  </td>
                </tr>
              );
            })}
            {!competitors.length && !loading && (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
                No competitors yet. Click "Add Competitor" to build this property's comp set (up to 5).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Competitor Rates comparison grid (dates × competitors + market + your rate) ── */}
      {(() => {
        const fmt = (n) => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
        const chanColor = (ch) => {
          const c = String(ch || '').toLowerCase();
          if (c.includes('booking')) return '#1e40af';
          if (c.includes('agoda')) return '#be185d';
          if (c.includes('expedia')) return '#b45309';
          if (c.includes('mmt') || c.includes('makemytrip') || c.includes('goibibo')) return '#dc2626';
          return '#64748b';
        };
        // Unique sorted dates and per-competitor map from the daily rollup
        const dates = Array.from(new Set(rateRows.map(r => r.stay_date))).sort();
        const byComp = new Map();
        rateRows.forEach(r => {
          if (!byComp.has(r.hotel_name)) byComp.set(r.hotel_name, {});
          byComp.get(r.hotel_name)[r.stay_date] = r;
        });
        const marketLow = {}; // date -> min lowest_rate across competitors
        dates.forEach(d => {
          const vals = rateRows.filter(r => r.stay_date === d && r.lowest_rate != null).map(r => Number(r.lowest_rate));
          marketLow[d] = vals.length ? Math.min(...vals) : null;
        });
        const cellDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
        return (
          <div style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0, color: 'var(--theme-primary-dark)' }}>Competitor Rates — next {rateDays} days</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>Horizon</label>
                <select value={rateDays} onChange={e => setRateDays(Number(e.target.value))} style={{ ...inputStyle, width: 'auto', padding: '0.3rem 0.5rem' }}>
                  {[7, 14, 30, 60].map(n => <option key={n} value={n}>{n} days</option>)}
                </select>
                <button onClick={loadRates} disabled={rateLoading} style={{ padding: '0.35rem 0.8rem', background: '#fff', border: '1px solid #cbd5e0', borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem' }}>{rateLoading ? 'Loading…' : 'Refresh'}</button>
              </div>
            </div>
            <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
              Each cell shows the competitor's <b>lowest OTA rate</b> for that stay date, with the <b>cheapest OTA channel</b> below it (hover for median/high). Type your own rate in the last row to compare against the market low — green = you're cheaper, red = dearer. (Your rate is not saved.)
            </p>
            {dates.length === 0 ? (
              <div style={{ padding: '1.2rem', textAlign: 'center', color: 'var(--theme-text-secondary)', background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, fontSize: '0.88rem' }}>
                No competitor rates collected yet for this window. Once the Rate Data Service shops your competitors, rates appear here.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
                <table style={{ borderCollapse: 'collapse', background: '#fff', fontSize: '0.8rem', minWidth: 520 }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                      <th style={{ ...rateTh, position: 'sticky', left: 0, background: 'var(--theme-primary)', zIndex: 1, textAlign: 'left' }}>Competitor</th>
                      {dates.map(d => <th key={d} style={rateTh}>{cellDate(d)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(byComp.entries()).map(([name, byDate]) => (
                      <tr key={name} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ ...rateTd, fontWeight: 600, position: 'sticky', left: 0, background: '#fff', textAlign: 'left', whiteSpace: 'nowrap' }}>{name}</td>
                        {dates.map(d => {
                          const c = byDate[d];
                          if (!c) return <td key={d} style={{ ...rateTd, color: '#cbd5e0' }}>—</td>;
                          if (c.any_sold_out && c.lowest_rate == null) return <td key={d} style={{ ...rateTd, color: '#b45309', fontWeight: 600 }}>Sold out</td>;
                          return (
                            <td key={d} style={{ ...rateTd, lineHeight: 1.15 }} title={`${c.lowest_channel || ''} · median ${fmt(c.median_rate)} · high ${fmt(c.highest_rate)}`}>
                              <div style={{ fontWeight: 600 }}>{fmt(c.lowest_rate)}</div>
                              {c.lowest_channel && <div style={{ fontSize: '0.64rem', fontWeight: 600, color: chanColor(c.lowest_channel), marginTop: 1 }}>{c.lowest_channel}</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Market low summary */}
                    <tr style={{ borderTop: '2px solid var(--theme-border)', background: 'var(--theme-bg-light)' }}>
                      <td style={{ ...rateTd, fontWeight: 700, position: 'sticky', left: 0, background: 'var(--theme-bg-light)', textAlign: 'left' }}>Market Low</td>
                      {dates.map(d => <td key={d} style={{ ...rateTd, fontWeight: 700, color: '#1e3a5f' }}>{fmt(marketLow[d])}</td>)}
                    </tr>
                    {/* Your rate (editable, session-only) */}
                    <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ ...rateTd, fontWeight: 700, position: 'sticky', left: 0, background: '#fff', textAlign: 'left' }}>Your Rate</td>
                      {dates.map(d => {
                        const yr = yourRates[d];
                        const mk = marketLow[d];
                        let color = 'inherit';
                        if (yr && mk != null) color = Number(yr) <= mk ? '#166534' : '#b91c1c';
                        return (
                          <td key={d} style={{ ...rateTd, padding: '0.2rem' }}>
                            <input value={yr || ''} onChange={e => setYourRates({ ...yourRates, [d]: e.target.value.replace(/[^\d]/g, '') })}
                              placeholder="—" inputMode="numeric"
                              style={{ width: 64, padding: '0.25rem', border: '1px solid #dcdde1', borderRadius: 4, fontSize: '0.78rem', textAlign: 'right', color, fontWeight: 600 }} />
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

const inputStyle = { padding: '0.5rem', borderRadius: 6, border: '1px solid #dcdde1', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const rateTh = { padding: '0.4rem 0.6rem', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' };
const rateTd = { padding: '0.35rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap', color: '#1e293b' };
function Field({ label, children }) {
  return (
    <div style={{ display: 'grid', gap: '0.25rem' }}>
      <label style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--theme-text)' }}>{label}</label>
      {children}
    </div>
  );
}

export default PmsRevenueManager;
